import { getDefaultStore } from "jotai";
import { userAtom } from "./state";
import axiosInstance from "./axiosInstance";
import * as Sentry from "@sentry/browser";

export async function signup(fullName, email, password, recaptcha, referralId, country) {
  try {
    const requestBody = {
      full_name: fullName,
      email: email,
      password: password,
      recaptcha_token: recaptcha,
      referral_id: referralId,
    };
    
    // Only add country if it's provided and not empty
    if (country && country.trim() !== '') {
      requestBody.country = country;
    }
    
    const response = await axiosInstance.post("/users/register", requestBody, {
      timeout: 60000 // 1 minute timeout
    });

    console.log("User registered successfully:", response.data);

    const store = getDefaultStore();
    store.set(userAtom, (prev) => ({
      ...prev,
      user: { ...response.data, full_name: fullName },
    }));

    if (window.trackUserSignup) {
      window.trackUserSignup(response.data.id);
    }

    return {
      success: true,
    };
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error registering user:", error);

    if (
      error.response?.status === 400 &&
      error.response?.data?.detail === "REGISTER_USER_ALREADY_EXISTS"
    ) {
      return {
        success: false,
        error: "An account with this email already exists",
      };
    }

    // More detailed error message
    let errorMessage = "Registration failed. Please try again.";
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

export async function login(email, password) {
  try {
    const formData = new FormData();
    formData.set("username", email);
    formData.set("password", password);
    const response = await axiosInstance.post("/users/login", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    console.log("User logged in successfully:", response.data);

    const store = getDefaultStore();

    store.set(userAtom, (prev) => ({
      ...prev,
      isAuthenticated: true,
      accessToken: response.data.access_token,
    }));

    return true;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error loggin in user:", error);

    return false;
  }
}

export async function getCurrentUser() {
  try {
    const response = await axiosInstance.get("/users/me");
    // console.log("Got user info:", response.data);

    const store = getDefaultStore();
    const currentUser = store.get(userAtom);

    // Only update if the user data has changed, otherwise we trigger an undless loop in protected routes
    if (JSON.stringify(currentUser.user) !== JSON.stringify(response.data)) {
      store.set(userAtom, (prev) => ({
        ...prev,
        user: response.data,
        isAuthenticated: true,
      }));

      // Update Crisp with user info
      if (window.updateCrispUser) {
        window.updateCrispUser(response.data);
      }
    }

    return true;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error getting user data:", error);
    return false;
  }
}

export async function logout() {
  console.log("Logout called. Logging out user");
  // Clear access token and ID token from local storage
  const store = getDefaultStore();
  store.set(userAtom, (prev) => ({
    ...prev,
    isAuthenticated: false,
    user: null,
    accessToken: null,
  }));
}

export async function isAuthenticated() {
  const store = getDefaultStore();

  const user = store.get(userAtom);

  if (!user) return false;
  return user.isAuthenticated;
}

export async function forgotPassword(email) {
  try {
    const response = await axiosInstance.post("/users/forgot-password", {
      email: email,
    });

    console.log("Forgot password link sent successfully:", response.data);

    return true;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error sending forgot password link:", error);

    return null;
  }
}

export async function resetPassword(password, token) {
  try {
    const response = await axiosInstance.post("/users/reset-password", {
      password: password,
      token: token,
    });

    console.log("Reset password completed successfully:", response.data);

    return true;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error sending reset password:", error);

    return null;
  }
}
